// Top-level state machine for the web shell.
//
//   - URL is /set-password?token=...    → standalone SetPasswordPage
//                                          (handles both invite + reset
//                                          consume flows)
//   - no users yet (initial install)    → signup form (creates the first
//                                          admin)
//   - users exist, no stored session    → login form (or forgot-password
//                                          form when the user clicks the
//                                          "Forgot password?" link).
//                                          Public signup is disabled —
//                                          new accounts are created by an
//                                          admin via the User Management
//                                          page.
//   - valid stored session              → workspace
import { useCallback, useEffect, useMemo, useState } from "react";
import { httpClient } from "./client/http-client";
import { type StoredSession, jwtStore } from "./client/jwt-store";
import { AuthLayout } from "./features/auth/AuthLayout";
import { ForgotPasswordPage } from "./features/auth/ForgotPasswordPage";
import { LoginPage } from "./features/auth/LoginPage";
import { SetPasswordPage } from "./features/auth/SetPasswordPage";
import { SignupPage } from "./features/auth/SignupPage";
import { WorkspacePage } from "./features/workspace/WorkspacePage";
import { type ThemeMode, applyTheme, readTheme, writeTheme } from "./lib/theme";

type AuthView = "login" | "forgotPassword";

interface SetPasswordRoute {
	kind: "set-password";
	token: string;
}

function readSetPasswordRoute(): SetPasswordRoute | null {
	if (typeof window === "undefined") return null;
	if (window.location.pathname !== "/set-password") return null;
	const token = new URLSearchParams(window.location.search).get("token") ?? "";
	return { kind: "set-password", token };
}

export const App = () => {
	const [session, setSession] = useState<StoredSession | null>(() =>
		jwtStore.get(),
	);
	// null = haven't probed yet; true = no users exist (show signup form);
	// false = users exist (login only)
	const [setupRequired, setSetupRequired] = useState<boolean | null>(null);
	const [themeMode, setThemeMode] = useState<ThemeMode>(() => readTheme());
	const [authView, setAuthView] = useState<AuthView>("login");

	// One-shot read of the current URL for the /set-password short-circuit.
	// We don't need a router; this page is reached via an emailed link, the
	// user submits once, and we redirect to /. If the URL ever needs to
	// change at runtime, we'd swap to a router.
	const setPasswordRoute = useMemo(readSetPasswordRoute, []);

	// Apply the stored theme on first mount and whenever it changes.
	useEffect(() => {
		applyTheme(themeMode);
	}, [themeMode]);

	// Probe setup-required when there's no session. A signed-in user shouldn't
	// pay the cost on every page load. Skip the probe entirely on the
	// set-password page — the recipient may not even have a session yet.
	useEffect(() => {
		if (session) return;
		if (setPasswordRoute) return;
		let cancelled = false;
		httpClient.auth
			.setupRequired()
			.then((res) => {
				if (!cancelled) setSetupRequired(res.required);
			})
			.catch(() => {
				// If the probe fails (network blip, orchestrator boot), default
				// to login-only — the safer fallback than offering signup.
				if (!cancelled) setSetupRequired(false);
			});
		return () => {
			cancelled = true;
		};
	}, [session, setPasswordRoute]);

	const toggleTheme = useCallback(() => {
		setThemeMode((prev) => {
			const next: ThemeMode = prev === "light" ? "dark" : "light";
			writeTheme(next);
			return next;
		});
	}, []);

	const handleAuthenticated = useCallback(() => {
		setSession(jwtStore.get());
	}, []);

	const handleLoggedOut = useCallback(() => {
		setSession(null);
		setAuthView("login");
		// After logout, re-probe in case the last user was just deleted (edge
		// case — clears back to "setup mode").
		setSetupRequired(null);
	}, []);

	if (setPasswordRoute) {
		return (
			<AuthLayout themeMode={themeMode} onToggleTheme={toggleTheme}>
				<SetPasswordPage token={setPasswordRoute.token} />
			</AuthLayout>
		);
	}

	if (!session) {
		return (
			<AuthLayout themeMode={themeMode} onToggleTheme={toggleTheme}>
				{setupRequired === null ? (
					<div className="tg-text-muted text-center text-[0.95rem]">
						Loading…
					</div>
				) : setupRequired ? (
					<SignupPage
						onAuthenticated={handleAuthenticated}
						isInitialSetup
					/>
				) : authView === "forgotPassword" ? (
					<ForgotPasswordPage
						onBackToLogin={() => setAuthView("login")}
					/>
				) : (
					<LoginPage
						onAuthenticated={handleAuthenticated}
						onForgotPassword={() => setAuthView("forgotPassword")}
					/>
				)}
			</AuthLayout>
		);
	}

	return (
		<WorkspacePage
			session={session}
			themeMode={themeMode}
			onToggleTheme={toggleTheme}
			onLoggedOut={handleLoggedOut}
		/>
	);
};
